import { Injectable } from '@angular/core';
import { Router, CanActivate } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { ToastrService } from 'ngx-toastr';

import { AdminService } from './admin.service';
import { UserService } from './user.service';

import { AppSettings } from './../app.settings';
import { SidekickService } from './sidekick.service';

interface AuthAndSubInfo {
	isAuthenticated: boolean;
	hasSubscription: boolean;
}

@Injectable()
export class AuthGuardService implements CanActivate {

	constructor(
		private router: Router,
		private cookieService: CookieService,
		private toastr: ToastrService,
		private adminService: AdminService,
		private sideKickService: SidekickService,
		private userService: UserService
	) { }

	// Checking whether user can enter main page
	// Both Authentication & Subscription are required for canActivate
	canActivate(): Promise<boolean> {
		// Handle function after checking authentication and subscription
		const handle = (isAuth, hasSub): boolean => {
			if (isAuth && hasSub) {
				console.log('authenticated & subscribed.');
				return true;
			} else {
				console.log('auth failed');
				this.router.navigate(['auth/login'], {
					queryParamsHandling: 'merge'
				}); // To keep socket sidekick params
				// Alert user there is no subscription of this app
				if (isAuth && (!hasSub)) {
					this.toastr.error('No Subscription.');
				}
				return false;
			}
		};
		return new Promise((resolve) => {
			console.log('Checking Auth...');
			// Different auth checks for different accessing portal
			let checkPromise: Promise<AuthAndSubInfo>;
			switch (AppSettings.loginPortal) {
				case 'LBPortal':
					checkPromise = this.lbPortalAuthCheck();
					break;
				case 'AZPortal':
				default:
					checkPromise = this.azPortalAuthCheck();
			}
			checkPromise.then((info) => {
				resolve(handle(info.isAuthenticated, info.hasSubscription));
			});
			// // Default: no authentication nor subscription
			// const info = {
			// 	isAuthenticated: false,
			// 	hasSubscription: false
			// };
			// // Check authentication first
			// if (!this.userService.isTokenExpired()) {
			// 	// Token exists => app is authenticated
			// 	info.isAuthenticated = true;
			// 	// Check subscription if authenticated
			// 	this.adminService.getUserSubscription(this.userService.getUserInfo().userEmail, (rsp) => {
			// 		// Has subscription
			// 		if (rsp.data && rsp.data[0]) {
			// 			info.hasSubscription = true;
			// 			// Update userInfo with subscription info
			// 			this.userService.updateUserInfoFromUserSub(rsp.data[0]);
			// 		}
			// 		resolve(handle(info.isAuthenticated, info.hasSubscription));
			// 	}, (err) => {
			// 		resolve(handle(info.isAuthenticated, info.hasSubscription));
			// 	});
			// } else {
			// 	resolve(handle(info.isAuthenticated, info.hasSubscription));
			// }
		});
	}

	// Authentication check for AZPortal
	azPortalAuthCheck(): Promise<AuthAndSubInfo> {
		return new Promise<AuthAndSubInfo>(async (resolve) => {
			// Default: no authentication nor subscription
			const info = {
				isAuthenticated: false,
				hasSubscription: false
			};
			// Check authentication first
			if (!this.userService.isTokenExpired()) {
				// Token exists => app is authenticated
				info.isAuthenticated = true;
				// Check subscription if authenticated
				try {
					const userSubs = await this.adminService.getUserSubscription(this.userService.getUserInfo().userEmail);
					// Has subscription
					if (userSubs[0]) {
						info.hasSubscription = true;
						// Update userInfo with subscription info
						this.userService.updateUserInfoFromUserSub(userSubs[0]);
					}
				} catch (err) {
					console.log(err);
				}
				resolve(info);
			} else {
				resolve(info);
			}
		});
	}
	// Authentication check for LBPortal
	lbPortalAuthCheck(): Promise<AuthAndSubInfo> {
		return new Promise<AuthAndSubInfo>(async (resolve) => {
			// Default: no authentication nor subscription
			const info = {
				isAuthenticated: false,
				hasSubscription: false
			};
			// If cached token isn't expired yet, setUpdatedToken to set user info (setUserInfoFromToken)
			if (!this.userService.isTokenExpired()) {
				await this.userService.setUpdatedToken(this.userService.getCurrentToken());
			}
			// Check token from url
			this.sideKickService.checkTokenParam();
			if (this.sideKickService.tokenParam) {
				await this.userService.setUpdatedToken(this.sideKickService.tokenParam);
			}
			if (this.userService.isTokenExpired()) {
				// The cookie that stores the token info from LBPortal
				// If this cookie exist, update token
				const authCookieName = 'PortalToken';
				const tokenCookie = this.cookieService.get(authCookieName);
				if (tokenCookie) {
					await this.userService.setUpdatedToken(tokenCookie);
				}
			}
			// Check authentication first
			if (!this.userService.isTokenExpired()) {
				// Token exists => app is authenticated
				info.isAuthenticated = true;
				// Check subscription if authenticated
				try {
					const userSubs = await this.adminService.getUserSubscription(this.userService.getUserInfo().userEmail);
					// Has subscription
					if (userSubs[0]) {
						info.hasSubscription = true;
						// Update userInfo with subscription info
						this.userService.updateUserInfoFromUserSub(userSubs[0]);
					}
				} catch (err) {
					console.log(err);
				}
				resolve(info);
			} else {
				resolve(info);
			}
		});
	}

}
